<script setup lang="ts">
/**
 * /login —— 监管端登录(S-11;版式按 v2 · G4 重排,S-48)。
 *
 * 页面只做一件事:账号密码换令牌。**失败的话照抄服务端那句** ——
 * 它的 401 故意不区分「用户名不存在」「密码不对」「这是运营账号」(区分了就是
 * 一台账号枚举器),前端编一句更友好的话反而会把那条线抹掉。
 *
 * 账号从哪来:运营端「监管端账号」页开户(M-07)。这个端自己开不了账号 ——
 * 「谁开的这个账号」正是这份账号体系唯一比手改库强的地方。
 * 忘了密码的通道是**命令行**(Y-13:create-ops-account.mjs 的 --reset 支持 gov 账号),
 * 所以卡上那句「忘记密码请联系运营对接人」是有背书的:对接人就是能跑到那条命令的人。
 *
 * 错误只走 ErrorBanner,不开 toast:横幅就在表单上方,失败那一秒再弹一条一模一样的
 * 句子只是把同一件事说两遍。
 *
 * ═══ 这一页为什么自带 LoginLayout(G4 的版式)═══
 *
 * 与运营端登录页同款:O8 / G4 的文案是**整页**的 —— 品牌区的端名、副标题、
 * 三条要点,登录卡的标题、说明、两个字段与页脚。它们全部住在这个文件里
 * (向下传给 LoginLayout 的 props),于是 design-copy 的那一帧 surfaces 只需一个文件:
 * 文案只有一处来源,换版式只在 LoginLayout 里发生,换文案只在这里。
 *
 * ═══ 品牌区第三条要点的措辞(S-48 的裁决,记在 assumptions)═══
 *
 * 画板原文是「每一次查看都留痕」。审计事件枚举(AUDIT_EVENT_TYPE)里**没有**
 * 任何监管端查看/读取类事件 —— 照字面落就是一句没有机械支撑的承诺。
 * 这一条改成「看不到任何金额字段」:它是这个端真正承诺得起的那件事(L0-01、M-07),
 * 而且正是海关/边检最需要知道的一条。原句与推翻条件记在 assumptions 里。
 */
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NButton, NCollapse, NCollapseItem, NForm, NFormItem, NInput } from 'naive-ui'
import { ErrorBanner, LoginLayout } from '@sea-drone/ui'
import { api, ApiError, setToken } from '../api/client'

/**
 * G4 品牌区底端那三条要点。前两条是画板逐字;
 * 第三条按本切片的措辞裁决替换(理由见文件头),替换的记账在 assumptions.json。
 */
const POINTS = ['全程只读,数据不出域', '凭证一键回溯,支持证据归档', '看不到任何金额字段']

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const busy = ref(false)
const error = ref<string | null>(null)

async function submit(): Promise<void> {
  if (username.value.trim() === '' || password.value === '') {
    error.value = '账号和密码都要填'
    return
  }
  busy.value = true
  error.value = null
  try {
    const result = await api.login(username.value.trim(), password.value)
    setToken(result.token)
    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/dashboard'
    await router.push(redirect)
  } catch (e) {
    error.value =
      e instanceof ApiError
        ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
        : e instanceof Error
          ? e.message
          : String(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <LoginLayout
    tone="dark"
    product="监管端"
    tagline="面向海关、边检、海事、港航、公安、应急的只读监管入口。"
    :points="POINTS"
  >
    <div class="card-head">
      <h1 class="card-title">登录监管端</h1>
      <span class="readonly-tag">全程只读</span>
    </div>
    <p class="card-note">账号由平台运营开通,不支持自助注册。忘记密码请联系运营对接人。</p>

    <ErrorBanner v-if="error" class="banner" tone="dark" :message="error" title="登录失败" />

    <n-form class="form" @submit.prevent="submit">
      <n-form-item label="账号">
        <n-input v-model:value="username" size="large" placeholder="监管端账号" @keyup.enter="submit" />
      </n-form-item>
      <n-form-item label="密码">
        <n-input
          v-model:value="password"
          type="password"
          size="large"
          show-password-on="click"
          placeholder="密码"
          @keyup.enter="submit"
        />
      </n-form-item>
      <n-button type="primary" size="large" block :loading="busy" @click="submit">登录</n-button>
    </n-form>

    <!--
      这一段的原文来自旧页面(S-11)的折叠说明,只做了两处随事实的更新:
      这句「忘了密码」的通道写清是命令行重设(Y-13)。默认不展开 ——
      天天登录的人不需要每次读一遍;画板没画它,但不构成「把它删掉」的指令
      (失败态/加载态同样照现有行为保留,画板没画不是删的理由)。
    -->
    <n-collapse class="hint">
      <n-collapse-item title="这个端能看什么、不能看什么" name="scope">
        <p class="hint-text">
          账号由运营端开户与停用(M-07);忘了密码由平台运营用命令行重设一次(Y-13),
          本端没有自助修改入口。全程只读:看得到订单流向、货品照片与统计,
          <strong>看不到任何金额字段</strong>,也写不了任何业务数据。
          令牌 12 小时过期,过期或被停用后回到这一页。
        </p>
      </n-collapse-item>
    </n-collapse>

    <p class="card-foot">登录即表示你已知悉数据使用范围</p>
  </LoginLayout>
</template>

<style scoped>
/* 卡片本身由 LoginLayout 画(400px 居中),这里只管卡内的排版 */
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.card-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--sd-text1);
}

/* 只读小标签(画板:小圆角、灰底灰字) */
.readonly-tag {
  flex: none;
  padding: 3px 8px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
  border: 1px solid var(--sd-border);
  font-size: 11px;
  color: var(--sd-text3);
}

.card-note {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.banner {
  margin-top: 16px;
}

.form {
  margin-top: 16px;
}

.hint {
  margin-top: 16px;
}

.hint-text {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.7;
}

/* 页脚说明:画板逐字,居中小字 */
.card-foot {
  margin: 16px 0 0;
  text-align: center;
  font-size: 12px;
  color: var(--sd-text3);
}
</style>
